import React from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, ZoomControl, AttributionControl, Rectangle } from 'react-leaflet';
import StateOutlines from './StateOutlines';
import RegionOutlinesMA from './RegionOutlinesMA';

export default function MarkerMaps(props) {
  const { selectedState, markers, mapTitle, regionColors=[], geo="drought", markerType="circle", mapInfoMoreText=null } = props;
  const iconSize = 5;

  const stateBbox = {
    'ny': [-79.76338,40.49622,-71.85616,45.01597],
    'vt': [-73.43545,42.72694,-71.49364,45.01597],
    'nh': [-72.55248,42.69692,-70.71118,45.30586],
    'me': [-71.08446,43.07085,-66.98549,47.45681],
    'ma': [-73.50006,41.23893,-69.92871,42.88474],
    'ct': [-73.72618,40.99194,-71.78796,42.04940],
    'ri': [-71.85616,41.14717,-71.12036,42.01853],
    'pa': [-80.52072,39.72006,-74.69888,42.26896],
    'nj': [-75.56031,38.93103,-73.90206,41.35472],
    'de': [-75.79003,38.45161,-75.05063,39.83927],
    'md': [-79.48700,37.92245,-75.05063,39.72263],
    'wv': [-82.64199,37.20203,-77.71748,40.63859],
    'nedews': [-80.2,40.2,-66.6,47.7],
    'northeast': [-82.70, 37.20, -66.90, 47.60]
  }
  const feature_names = {
    'drought': 'Drought Regions',
    'MAJBAS_POLY20': 'Drainage Basins',
    'COUNTIESSURVEY_POLYM_GENCOAST': 'Counties',
    'Fire Control Districts': 'Fire Control Districts',
  }
  const bbox = stateBbox[selectedState];
  const bounds = [[bbox[1], bbox[0]], [bbox[3], bbox[2]]];
  const minZoomLevel = 7;
  const maxBounds = [[37.20, -82.70], [48.60, -65.00]];
  const statesOnMap = ['ny','vt','nh','me','ma','ct','ri','pa','nj','md','de','wv'];

  const geoStyle = {color: "gray", weight:0, fillColor: "gray", fillOpacity: 0.3};
  const geoStyle_selected = {color: "darkblue", weight:1, fillColor: "white", fillOpacity: 0.8};
  const mapInfoText = geo === 'drought' ? feature_names['drought'] : (typeof geo === "object" && geo.name && feature_names[geo.name] ? feature_names[geo.name] : geo.name);
  const gs = 0.125;


  // Define the square marker icon
  const getSquareIcon = (size, fillColor) => {
    return L.divIcon({
      className: '',  //removes default background/border
      html: `<div style="
        background-color: ${fillColor}; 
        width: ${size*2-2}px; 
        height: ${size*2-2}px; 
        box-shadow: none; 
        border: 1px solid black;
      "></div>`,
      iconSize: [size*2-2, size*2-2], // Dynamic [width, height]
      iconAnchor: [size, size], // Center the icon on the coordinate
    });
  };

  return (
    <div className="marker-map-container">
      <MapContainer
        className="marker-map"
        bounds={bounds}
        maxBounds={maxBounds}
        minZoom={minZoomLevel}
        scrollWheelZoom={false}
        attributionControl={false}
        zoomControl={false}>

        <TileLayer
          url='https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png'
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          crossOrigin={true}
          onTileLoadStart={te => { te.tile.setAttribute('alt', 'Leaflet map image');}}
        />

        <AttributionControl position="bottomright" prefix={"<a href='https://leafletjs.com'>Leaflet</a>"} />

        <ZoomControl position="topright" />
        
        <StateOutlines 
          statesOnMap={statesOnMap.filter(item => item !== selectedState)}
          mapStyle={geoStyle}
        />
        {selectedState !== 'ma' &&
          <StateOutlines
            statesOnMap={[selectedState]}
            mapStyle={geoStyle_selected}
          />
        }
        {selectedState === 'ma' &&
          <RegionOutlinesMA 
            geo={geo}
            mapStyle={geoStyle_selected}
            fillColors={regionColors}
          />
        }

        {markers && markerType === 'circle' &&
          markers.map((m, i) => (
            m.icon === "square" ?
              <Marker 
                key={i+"marker"}
                position={m.position}
                icon={getSquareIcon(m.iconsize ? m.iconsize : iconSize, m.fillColor)} 
              >
                {m.data &&
                  <Popup>
                    {m.data.length === 1 ? (
                      <div>{m.data[0]}</div>
                    ) : (
                      <div className="marker-map-popup">{m.data[0]}<br/>{m.data[1]}</div>
                    )}
                  </Popup>
                }
              </Marker>
           :
              <CircleMarker 
                key={i+"marker"}
                center={m.position}
                radius={m.iconsize ? m.iconsize : iconSize}
                {...{fillColor: m.fillColor, color: "black", weight: 1, fillOpacity: 1}}
              >
                {m.data &&
                  <Popup>
                    {m.data.length === 1 ? (
                      <div>{m.data[0]}</div>
                    ) : (
                      <div className="marker-map-popup">{m.data[0]}<br/>{m.data[1]}</div>
                    )}
                  </Popup>
                }
              </CircleMarker>
          ))
        } 

        {markers && markerType === 'rectangle' &&
          markers.map((m, i) => (
            <Rectangle
              key={i+"rect"}
              bounds={[[m.position[0]-gs*0.5, m.position[1]-gs*0.5], [m.position[0]+gs*0.5, m.position[1]+gs*0.5]]}             
              {...{fillColor: m.fillColor, color: "lightgray", weight: 0.0, fillOpacity: 0.8}}
            >
              {m.data &&
                <Popup>
                  <div>{m.data}</div>
                </Popup>  
              }            
            </Rectangle>
          ))
        }

      </MapContainer>

      <span className="marker-map-title">{mapTitle}</span>
      <span className="marker-map-info">Outlines are {mapInfoText}<br />{mapInfoMoreText}</span>
    </div>
  )
}
